import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Camera, CheckCircle2, Loader2,
  ImagePlus, X, Play, Flag, AlertTriangle,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import { buildRoute } from '@/config/routes'
import WorkerLayout from '@/components/worker/WorkerLayout'
import Button from '@/components/ui/Button'

// ── Photo section component ───────────────────────────────────────────────────

function PhotoSection({ title, subtitle, type, bookingId, workerId, photos, onAdded }) {
  const inputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState(null)

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    setErr(null)

    const ext  = file.name.split('.').pop() || 'jpg'
    const path = `${workerId}/${bookingId}/${type}_${Date.now()}.${ext}`

    const { error: upErr } = await supabase.storage
      .from('job-photos')
      .upload(path, file, { upsert: false })

    if (upErr) { setErr('Upload failed. Try again.'); setUploading(false); return }

    const { error: rowErr } = await supabase
      .from('booking_photos')
      .insert({
        booking_id:  bookingId,
        uploaded_by: workerId,
        photo_type:  type,
        storage_path: path,
      })

    if (rowErr) { setErr('Save failed. Try again.'); setUploading(false); return }

    onAdded({ storage_path: path, photo_type: type })
    setUploading(false)
    // Reset so the same file can be reselected if needed
    e.target.value = ''
  }

  function getUrl(path) {
    const { data: { publicUrl } } = supabase.storage.from('job-photos').getPublicUrl(path)
    return publicUrl
  }

  return (
    <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
      <p className="text-sm font-semibold text-ink-primary mb-0.5">{title}</p>
      {subtitle && <p className="text-xs text-ink-muted mb-3">{subtitle}</p>}

      {/* Grid of uploaded photos */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {photos.map((p, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-surface-overlay">
              <img
                src={getUrl(p.storage_path)}
                alt={`${type} photo ${i + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-1 right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-3 h-3 text-white" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {err && <p className="text-xs text-siglat-red mb-2">{err}</p>}

      {/* Upload button */}
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-surface-border text-sm text-ink-secondary hover:border-siglat-teal hover:text-siglat-teal transition-colors w-full justify-center disabled:opacity-50"
      >
        {uploading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
          : <><ImagePlus className="w-4 h-4" /> {photos.length > 0 ? 'Add Another' : 'Take / Upload Photo'}</>
        }
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function OnSite() {
  const { id } = useParams()
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [job,      setJob]      = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [acting,   setActing]   = useState(false)
  const [err,      setErr]      = useState(null)
  const [available, setAvailable] = useState(false)

  // Photos keyed by type
  const [beforePhotos, setBeforePhotos] = useState([])
  const [duringPhotos, setDuringPhotos] = useState([])
  const [afterPhotos,  setAfterPhotos]  = useState([])

  useEffect(() => {
    if (!profile?.id) return
    fetchJob()
    fetchAvailability()
  }, [profile?.id, id]) // eslint-disable-line

  async function fetchAvailability() {
    const { data } = await supabase
      .from('worker_profiles')
      .select('is_available')
      .eq('id', profile.id)
      .maybeSingle()
    if (data) setAvailable(data.is_available ?? false)
  }

  async function fetchJob() {
    setLoading(true)
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        id, ref_number, status, address,
        service_categories ( name ),
        booking_photos ( photo_type, storage_path )
      `)
      .eq('id', id)
      .eq('worker_id', profile.id)
      .single()

    if (error || !data) {
      setErr('Job not found.')
      setLoading(false)
      return
    }

    setJob(data)

    const bps = data.booking_photos ?? []
    setBeforePhotos(bps.filter((p) => p.photo_type === 'before'))
    setDuringPhotos(bps.filter((p) => p.photo_type === 'during'))
    setAfterPhotos(bps.filter((p) => p.photo_type === 'after'))

    setLoading(false)
  }

  function addPhoto(photo) {
    const type = photo.photo_type
    if (type === 'before')  setBeforePhotos((prev) => [...prev, photo])
    if (type === 'during')  setDuringPhotos((prev) => [...prev, photo])
    if (type === 'after')   setAfterPhotos((prev) => [...prev, photo])
  }

  async function startWork() {
    if (beforePhotos.length === 0) {
      setErr('Please take at least one "Before" photo first.')
      return
    }
    setErr(null)
    setActing(true)
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'in_progress', started_at: new Date().toISOString() })
      .eq('id', id)
      .eq('worker_id', profile.id)
    if (error) { setErr('Failed to update status. Try again.'); setActing(false); return }
    setJob((prev) => ({ ...prev, status: 'in_progress' }))
    setActing(false)
  }

  async function markComplete() {
    if (afterPhotos.length === 0) {
      setErr('Please take at least one "After" photo before completing.')
      return
    }
    setErr(null)
    setActing(true)
    const { error } = await supabase
      .from('bookings')
      .update({ status: 'pending_approval', completed_at: new Date().toISOString() })
      .eq('id', id)
      .eq('worker_id', profile.id)
    if (error) { setErr('Failed to complete job. Try again.'); setActing(false); return }
    navigate(buildRoute.workerComplete(id))
  }

  if (loading) {
    return (
      <WorkerLayout available={available}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
        </div>
      </WorkerLayout>
    )
  }

  if (err && !job) {
    return (
      <WorkerLayout available={available}>
        <div className="px-4 py-10 text-center max-w-sm mx-auto">
          <AlertTriangle className="w-8 h-8 text-siglat-red mx-auto mb-3" />
          <p className="text-sm text-ink-secondary">{err}</p>
          <button
            onClick={() => navigate('/worker')}
            className="mt-4 text-sm text-siglat-teal underline underline-offset-2"
          >
            Back to dashboard
          </button>
        </div>
      </WorkerLayout>
    )
  }

  const isStarted   = job?.status === 'in_progress'
  const category    = job?.service_categories?.name ?? 'Service'

  return (
    <WorkerLayout available={available}>
      <div className="max-w-lg mx-auto px-4 py-5 flex flex-col gap-4">

        {/* ── Back ─────────────────────────────────────────────────────────── */}
        <button
          onClick={() => navigate(buildRoute.workerJob(id))}
          className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-primary transition-colors w-fit"
        >
          <ArrowLeft className="w-4 h-4" />
          Job Details
        </button>

        {/* ── Header ───────────────────────────────────────────────────────── */}
        <div>
          <h1 className="text-lg font-bold text-ink-primary">{category}</h1>
          <p className="text-xs text-ink-muted font-mono">{job?.ref_number}</p>
          <p className="text-xs text-ink-muted mt-1">{job?.address}</p>
        </div>

        {/* ── Status bar ───────────────────────────────────────────────────── */}
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
          ${isStarted
            ? 'bg-siglat-teal/10 border border-siglat-teal/25 text-siglat-teal'
            : 'bg-yellow-500/10 border border-yellow-500/25 text-yellow-400'}`}
        >
          {isStarted
            ? <><Play className="w-4 h-4" /> Work is in progress</>
            : <><Camera className="w-4 h-4" /> Take before photos, then start work</>
          }
        </div>

        {/* ── Before photos ─────────────────────────────────────────────────── */}
        <PhotoSection
          title="Before Photos"
          subtitle="Required before starting work"
          type="before"
          bookingId={id}
          workerId={profile.id}
          photos={beforePhotos}
          onAdded={addPhoto}
        />

        {/* ── Start work button ─────────────────────────────────────────────── */}
        {!isStarted && (
          <Button
            onClick={startWork}
            disabled={acting || beforePhotos.length === 0}
            className="w-full py-4 text-base"
          >
            {acting
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</>
              : <><Play className="w-4 h-4" /> Start Work</>
            }
          </Button>
        )}

        {/* ── During + after photos (only after started) ────────────────────── */}
        {isStarted && (
          <>
            <PhotoSection
              title="During Photos"
              subtitle="Document work in progress (optional)"
              type="during"
              bookingId={id}
              workerId={profile.id}
              photos={duringPhotos}
              onAdded={addPhoto}
            />

            <PhotoSection
              title="After Photos"
              subtitle="Required before marking complete"
              type="after"
              bookingId={id}
              workerId={profile.id}
              photos={afterPhotos}
              onAdded={addPhoto}
            />

            {/* Error */}
            {err && (
              <div className="flex items-center gap-2 p-3 bg-siglat-red/10 border border-siglat-red/25 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-siglat-red shrink-0" />
                <p className="text-sm text-siglat-red">{err}</p>
              </div>
            )}

            <Button
              onClick={markComplete}
              disabled={acting || afterPhotos.length === 0}
              className="w-full py-4 text-base"
            >
              {acting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Completing…</>
                : <><Flag className="w-4 h-4" /> Mark as Complete</>
              }
            </Button>
          </>
        )}

        {/* Non-started error */}
        {!isStarted && err && (
          <div className="flex items-center gap-2 p-3 bg-siglat-red/10 border border-siglat-red/25 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-siglat-red shrink-0" />
            <p className="text-sm text-siglat-red">{err}</p>
          </div>
        )}

      </div>
    </WorkerLayout>
  )
}
